import React, { PropsWithChildren, ReactNode } from "react";
import styled from "styled-components";
import { Zap } from "lucide-react";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { PRIMARY_EXCHANGES } from "@shared/common/constants";
import { BINANCE_API_KEYS_PATH } from "@shared/features/onboarding/constants";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { QUICK_CONNECT_SUBMIT_BUTTON_ID } from "testIds.ts";
import { Hint } from "features/portfolio/CreateAccount/NewLook/styles";

interface NewLookQuickConnectProps {
  primaryExchange?: string;
  messages?: ReactNode;
  buttonText: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** How it works is explained beside the form: only exchange terms here. */
  explainedBeside?: boolean;
}

const NewLookQuickConnect = ({
  primaryExchange,
  messages,
  buttonText,
  onClick,
  loading,
  disabled,
  explainedBeside,
  children,
}: PropsWithChildren<NewLookQuickConnectProps>) => {
  const isBinance = primaryExchange === PRIMARY_EXCHANGES.BINANCE;
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);

  return (
    <Wrapper>
      {messages}
      <Button
        type="primary"
        $size="l"
        onClick={onClick}
        loading={loading}
        disabled={disabled}
        data-testid={QUICK_CONNECT_SUBMIT_BUTTON_ID}
        icon={<Zap size={16} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />}
        style={{ width: "100%" }}
      >
        {buttonText}
      </Button>
      {(!explainedBeside || isBinance) && (
        <Hint>
          {!explainedBeside &&
            `${t("create_account.new_look.quick_connect_hint", {
              exchange: primaryExchange,
            })} `}
          {isBinance && (
            <>
              {t("create_account.binance_terms_prefix")}{" "}
              <Link
                href={BINANCE_API_KEYS_PATH}
                target="_blank"
                style={{ fontSize: "inherit" }}
              >
                {t("create_account.binance_terms_link")}
              </Link>
              .
            </>
          )}
        </Hint>
      )}
      {children}
    </Wrapper>
  );
};

export default NewLookQuickConnect;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  /* A message above the button gets a little more room than the gap. */
  > [role="alert"] {
    margin-bottom: 4px;
  }
`;
